import { useRef, useEffect } from "react";
import PageMeta from "@/components/seo/PageMeta";
import JsonLd from "@/components/seo/JsonLd";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ScriptText from "@/components/ui/ScriptText";
import { yourHostContent, resolveImage } from "@/data/content";
import { useContent } from "@/hooks/useContent";

gsap.registerPlugin(ScrollTrigger);

export default function YourHostPage() {
  const content = useContent("pages/your-host", yourHostContent);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) return;

    const paragraphs = el.querySelectorAll(".host-paragraph");
    paragraphs.forEach((p) => {
      gsap.fromTo(
        p,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: p,
            start: "top 85%",
            toggleActions: "play none none none",
          },
        },
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach((st) => {
        const trigger = st.trigger;
        if (trigger && el.contains(trigger)) st.kill();
      });
    };
  }, []);

  return (
    <>
      <PageMeta
        title="Meet Your Host | Chasing Provence Women's Retreats"
        description="Meet the founder of Chasing Provence — intimate women's retreats in Provence, Tuscany, and the Cotswolds. A personal journey from California to the heart of Europe."
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Eryn",
          jobTitle: "Founder & Host",
          worksFor: {
            "@type": "Organization",
            name: "Chasing Provence",
          },
          description:
            "Founder and host of Chasing Provence, curating intimate women's retreats in Provence, Tuscany, and the Cotswolds.",
          url: "https://chasingprovence.com/your-host",
        }}
      />

      <div ref={ref} className="min-h-screen px-6 pt-32 pb-20 lg:px-12 lg:pt-40 lg:pb-32">
        <div className="mx-auto max-w-[680px]">
          {/* Header */}
          <div className="mb-12 text-center">
            <ScriptText className="mb-4 block text-3xl lg:text-4xl">
              {content.scriptAccent}
            </ScriptText>
            <h1 className="font-serif font-light text-soft-charcoal">
              {content.heading}
            </h1>
          </div>

          {/* Portrait */}
          {(content as { portrait?: string }).portrait && (
          <div className="host-paragraph mx-auto mb-16 aspect-[3/4] max-w-[400px] overflow-hidden rounded-sm shadow-xl ring-1 ring-warm-stone/20">
            <img
              src={resolveImage("/images/misc/", (content as { portrait?: string }).portrait)}
              alt="Your host"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          )}

          {/* Diary-style paragraphs */}
          <div className="space-y-10">
            {content.paragraphs.map((paragraph, i) => (
              <p
                key={i}
                className={`host-paragraph font-body text-base leading-[1.8] text-soft-charcoal/85 lg:text-lg ${
                  i === 0
                    ? "first-letter:float-left first-letter:mr-3 first-letter:font-serif first-letter:text-5xl first-letter:font-light first-letter:leading-none first-letter:text-faded-terracotta"
                    : ""
                }`}
              >
                {paragraph}
              </p>
            ))}
          </div>

          {/* Signature */}
          <div className="host-paragraph mt-14">
            <ScriptText className="text-5xl lg:text-6xl text-faded-terracotta">
              Eryn
            </ScriptText>
          </div>

          {/* Substack link */}
          <div className="host-paragraph mt-16 border-t border-warm-stone/30 pt-8 text-center">
            <ScriptText className="mb-3 block text-2xl">Follow Along</ScriptText>
            <a
              href={content.substackLink}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-sm tracking-widest uppercase text-faded-terracotta transition-colors hover:text-soft-charcoal"
            >
              {content.substackText} &rarr;
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
